import React, { useState } from "react";
import { Users, Check, ExternalLink, Info } from "lucide-react";

const TENANTTRAIL_FB_GROUP_URL = "https://www.facebook.com/groups/tenanttrail";

/**
 * FacebookGroupShareButton
 *
 * Drops a renter straight into the TenantTrail Facebook group's post
 * composer with a pre-filled message + the page URL + a tenanttrail.net
 * tag for SEO + recruitment back-flow.
 *
 * IMPORTANT (Feb 2026): Facebook killed 3rd-party Group posting APIs in
 * April 2024. We CANNOT auto-post on the user's behalf. We CANNOT toggle
 * anonymous posting from outside Facebook — that's a per-group admin
 * setting the founder enables in Group Settings → Anonymous posts.
 *
 * What this button does: opens the group in a new tab + copies the
 * pre-filled message to the clipboard. The user pastes into the FB
 * composer, optionally toggles "Post anonymously" if the group has it
 * enabled, then hits Post.
 *
 * Props:
 *   url    string  — link being shared (defaults to current page)
 *   title  string  — short title used in the composer text
 *   kind   string  — voice-note | q-and-a | comparison | other (for analytics)
 *   slug   string  — used in the share-clicks beacon
 *   hint   bool    — show the anonymous-posting hint underneath (default true)
 */
export default function FacebookGroupShareButton({
  url, title, kind = "other", slug = null, hint = true,
}) {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");

  const composerText = title
    ? `${title}\n\nFrom my TenantTrail vault: ${shareUrl}\n\n#TenantTrail #AusRenters`
    : `From my TenantTrail vault: ${shareUrl}\n\n#TenantTrail #AusRenters`;

  const beacon = () => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      if (!backend) return;
      fetch(`${backend}/api/share-clicks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          channel: "facebook-group",
          url: shareUrl,
          slug: slug || null,
          title: title || null,
          kind,
        }),
        keepalive: true,
      }).catch(() => { /* ignore */ });
    } catch (e) { /* ignore */ }
  };

  const onClick = async () => {
    beacon();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(composerText);
        setCopied(true);
        setTimeout(() => setCopied(false), 3500);
      }
    } catch (e) { /* clipboard may be blocked in iframe / non-https */ }
    // Open the TenantTrail group in a new tab. The user pastes the composer
    // text we just copied.
    window.open(TENANTTRAIL_FB_GROUP_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-1.5" data-testid="fb-group-share-wrap">
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-2 px-3 py-2 bg-[#1877F2] text-white text-sm font-serif rounded-sm hover:bg-[#155fbf] transition-colors"
        data-testid="fb-group-share-btn"
      >
        {copied ? (
          <>
            <Check size={14} strokeWidth={2.2} />
            <span>Copied. Paste in the group composer</span>
            <ExternalLink size={12} strokeWidth={1.75} />
          </>
        ) : (
          <>
            <Users size={14} strokeWidth={1.75} />
            <span>Share to TenantTrail FB Group</span>
            <ExternalLink size={11} strokeWidth={1.75} />
          </>
        )}
      </button>
      {hint && (
        <div className="flex items-start gap-1.5 text-[10px] text-[#6B7280] leading-relaxed">
          <Info size={10} strokeWidth={1.75} className="flex-shrink-0 mt-[1px]" />
          <span>
            Want to post without your name? In the group composer, tap <span className="font-mono">Post anonymously</span> before sharing. Available if the group admin has enabled it.
          </span>
        </div>
      )}
    </div>
  );
}
